import React, { useState, useEffect } from 'react';
import './PricingPage.scss';
import Tabs from '../../components/Tabs/Tabs';
import Button from '../../components/Button';
import { Link } from 'react-router-dom';
import { catalogData, operationalPricingData, constructionPricingData } from './pricingData';

const TABS = [
  { id: 'catalog', name: 'Danh mục công cụ' },
  { id: 'operational', name: 'Báo giá phí vận hành' },
  { id: 'construction', name: 'Báo giá phí thi công' }
];

const renderHtmlStyleText = (text) => {
  if (!text) return '';
  
  // Specific checks for multi-line items with unique bold prefix requirements
  // Item B: Dịch vụ cung cấp hạ tầng phần cứng...
  if (text.startsWith('Dịch vụ cung cấp hạ tầng phần cứng')) {
    const boldPart = 'Dịch vụ cung cấp hạ tầng phần cứng (thiết bị máy chủ, lưu trữ, an ninh bảo mật hệ thống, máy chủ GPU chuyên dụng xử lý đồ họa …).';
    if (text.includes(boldPart)) {
      const rest = text.replace(boldPart, '');
      return (
        <>
          <strong style={{ fontWeight: 'bold' }}>{boldPart}</strong>
          {rest}
        </>
      );
    }
  }

  // Item C: Dịch vụ hỗ trợ trong quá trình vận hành...
  if (text.startsWith('Dịch vụ hỗ trợ trong quá trình vận hành')) {
    const boldPart = 'Dịch vụ hỗ trợ trong quá trình vận hành';
    if (text.includes(boldPart)) {
      const rest = text.substring(boldPart.length);
      return (
        <>
          <strong style={{ fontWeight: 'bold' }}>{boldPart}</strong>
          {rest}
        </>
      );
    }
  }

  // Item D: Dịch vụ hỗ trợ trong quá trình thi công...
  if (text.startsWith('Dịch vụ hỗ trợ trong quá trình thi công')) {
    const boldPart = 'Dịch vụ hỗ trợ trong quá trình thi công';
    if (text.includes(boldPart)) {
      const rest = text.substring(boldPart.length);
      return (
        <>
          <strong style={{ fontWeight: 'bold' }}>{boldPart}</strong>
          {rest}
        </>
      );
    }
  }

  // General fallback: split at the first newline to bold the first line/title
  const parts = text.split('\n');
  if (parts.length > 0) {
    const title = parts[0];
    const rest = parts.slice(1).join('\n');
    return (
      <>
        <strong style={{ fontWeight: 'bold' }}>{title}</strong>
        {rest && <><br />{rest}</>}
      </>
    );
  }

  return text;
};

const PricingPage = () => {
  const [activeTab, setActiveTab] = useState('catalog');

  // Search state for provinces in Tab 2
  const [searchTerm, setSearchTerm] = useState('');
  
  // Calculator modal state
  const [showCalculator, setShowCalculator] = useState(false);
  
  // Calculator state
  const [selectedProvinceName, setSelectedProvinceName] = useState(operationalPricingData.provinces[0].name);
  const [calcMethod, setCalcMethod] = useState('province'); // 'province' (toàn tỉnh) or 'commune' (xã/phường)
  const [communesCount, setCommunesCount] = useState(operationalPricingData.provinces[0].total_communes);
  const [vpdkCount, setVpdkCount] = useState(1);
  const [calcBilling, setCalcBilling] = useState('yearly'); // 'yearly' or 'monthly'
  const [calcResult, setCalcResult] = useState({ subtotalCommunes: 0, subtotalVpdk: 0, total: 0 });

  // Get active province data
  const activeProvince = operationalPricingData.provinces.find(p => p.name === selectedProvinceName) || operationalPricingData.provinces[0];

  // Update calculator when province or pricing method changes
  useEffect(() => {
    if (activeProvince) {
      // Clamp communes count to province total when switching provinces
      if (communesCount > activeProvince.total_communes || communesCount === 0) {
        setCommunesCount(activeProvince.total_communes);
      }
    }
  }, [selectedProvinceName]);

  // Recalculate estimated pricing
  useEffect(() => {
    if (!activeProvince) return;

    let subtotalCommunes = 0;
    let subtotalVpdk = 0;
    let total = 0;

    if (calcMethod === 'province') {
      const price = calcBilling === 'yearly' ? activeProvince.prov_year : activeProvince.prov_month;
      if (typeof price === 'number') {
        total = price;
      } else {
        total = 0; // negotiable or "Sẽ báo giá cụ thể"
      }
    } else {
      // commune calculation
      const communePrice = calcBilling === 'yearly' ? activeProvince.commune_year : activeProvince.commune_month;
      const vpdkPrice = typeof activeProvince.vpdk_price === 'number' ? activeProvince.vpdk_price : 450;

      if (typeof communePrice === 'number') {
        subtotalCommunes = communesCount * communePrice;
      }
      
      // VPĐK is usually billed per year/unit, we assume it's flat per year or adjusted monthly
      const adjustedVpdkPrice = calcBilling === 'yearly' ? vpdkPrice : vpdkPrice / 12;
      subtotalVpdk = vpdkCount * adjustedVpdkPrice;

      total = subtotalCommunes + subtotalVpdk;
    }

    setCalcResult({
      subtotalCommunes,
      subtotalVpdk,
      total
    });
  }, [selectedProvinceName, calcMethod, communesCount, vpdkCount, calcBilling, activeProvince]);

  // Helper to format currency
  const formatCurrency = (value) => {
    if (value === 0 || value === undefined) return '0 Trđ';
    if (typeof value === 'string') return value;
    
    // Check if it's over 1000 million to convert to Billions (Tỷ)
    if (value >= 1000) {
      const billions = value / 1000;
      return billions.toLocaleString('vi-VN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Tỷ VNĐ';
    }
    
    return value.toLocaleString('vi-VN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Trđ';
  };

  const formatRawValue = (val) => {
    if (typeof val === 'number') {
      return val.toLocaleString('vi-VN', { maximumFractionDigits: 2 }) + ' Trđ';
    }
    return val; // negotiable text
  };

  // Render Checkmark SVG Icon for Tab 1
  const renderCheckIcon = () => (
    <span className="check-icon">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
    </span>
  );

  // Render Dash for Tab 1 empty cell
  const renderDash = () => <span className="dash-icon">—</span>;

  // Filtered provinces for Tab 2 search
  const filteredProvinces = operationalPricingData.provinces.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="pricing-page">
      {/* Hero Header */}
      <section className="pricing-hero">
        <div className="pricing-flare"></div>
        <div className="container">
          <h1>BẢNG GIÁ DỊCH VỤ VBDLIS</h1>
          <p>
            Cung cấp các giải pháp tối ưu cho việc biên tập, xây dựng, và vận hành Cơ sở dữ liệu Đất đai chính thức. 
            Linh hoạt theo quy mô từng dự án thi công và nhu cầu vận hành thực tế của từng địa phương.
          </p>
        </div>
      </section>

      {/* Sticky Tabs Navigation */}
      <div className="pricing-tabs-wrapper">
        <div className="container">
          <Tabs
            menu={TABS}
            selected={activeTab}
            onSelect={(id) => setActiveTab(id)}
          />
        </div>
      </div>

      {/* Main Content Area */}
      <main className="pricing-body">
        <div className="container">
          
          {/* TAB 1: DANH MỤC CÔNG CỤ */}
          {activeTab === 'catalog' && (
            <div className="catalog-tab animated fadeIn">
              <div className="catalog-intro">
                <h2>{catalogData.title}</h2>
              </div>

              <div className="matrix-table-card">
                <div className="table-responsive">
                  <table className="matrix-table">
                    <thead>
                      <tr>
                        <th style={{ width: '60px', textAlign: 'center' }}>STT</th>
                        <th style={{ minWidth: '280px' }}>Danh mục các dịch vụ cung cấp</th>
                        <th style={{ backgroundColor: 'rgba(0, 134, 255, 0.03)' }}>Phân hệ vận hành</th>
                        <th style={{ backgroundColor: 'rgba(0, 134, 255, 0.03)' }}>Phân hệ thi công</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Financial Policy Row */}
                      <tr style={{ backgroundColor: 'rgba(0, 134, 255, 0.03)', fontWeight: 'bold' }}>
                        <td className="cell-stt"></td>
                        <td className="cell-name" style={{ color: '#0060DF', fontWeight: '700' }}>{catalogData.financial_policy[1]}</td>
                        <td style={{ color: '#c62026', textAlign: 'center', fontSize: '12px', backgroundColor: 'rgba(0, 134, 255, 0.02)' }}>{catalogData.financial_policy[2]}</td>
                        <td style={{ color: '#c62026', textAlign: 'center', fontSize: '12px', backgroundColor: 'rgba(0, 134, 255, 0.02)' }}>{catalogData.financial_policy[3]}</td>
                      </tr>

                      {/* Service Items */}
                      {catalogData.items.map((item, index) => {
                        // Insert section title if it's the first item of a new section
                        const prevItem = index > 0 ? catalogData.items[index - 1] : null;
                        const showSectionHeader = !prevItem || prevItem.section !== item.section;

                        return (
                          <React.Fragment key={index}>
                            {showSectionHeader && (
                          <tr className="section-row">
                            <td colSpan="4">
                                  {item.section === 'PHÂN HỆ THI CÔNG VÀ VẬN HÀNH' ? `A. ${item.section}` : item.section}
                                </td>
                              </tr>
                            )}
                            <tr>
                              <td className="cell-stt">{item.stt}</td>
                              <td className="cell-name">{renderHtmlStyleText(item.name)}</td>
                              {item.marks.map((mark, mIdx) => (
                                <td key={mIdx} className="cell-mark">
                                  {mark.toLowerCase() === 'x' ? renderCheckIcon() : renderDash()}
                                </td>
                              ))}
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: BÁO GIÁ PHÍ VẬN HÀNH */}
          {activeTab === 'operational' && (
            <div className="operational-tab animated fadeIn">
              <div className="operational-grid">
                
                {/* Provinces List Table */}
                <div className="provinces-card">
                  <div className="table-header-actions">
                    <h3>Báo giá theo địa phương</h3>
                    <div className="actions-right">
                      <div className="search-wrapper">
                        <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input 
                          type="text" 
                          placeholder="Tìm kiếm tỉnh thành..." 
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <button className="calculator-toggle-btn" onClick={() => setShowCalculator(true)} title="Công cụ tính phí vận hành">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
                          <line x1="9" y1="22" x2="9" y2="16"></line>
                          <line x1="8" y1="6" x2="16" y2="6"></line>
                          <line x1="16" y1="22" x2="16" y2="16"></line>
                          <line x1="9" y1="16" x2="15" y2="16"></line>
                          <circle cx="9" cy="11" r="1"></circle>
                          <circle cx="15" cy="11" r="1"></circle>
                          <circle cx="9" cy="15" r="1"></circle>
                          <circle cx="15" cy="15" r="1"></circle>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="prov-table">
                      <thead>
                        <tr>
                          <th rowSpan="2" style={{ width: '40px' }}>STT</th>
                          <th rowSpan="2" style={{ minWidth: '130px' }}>Tỉnh/Thành Phố</th>
                          <th colSpan="3" style={{ textAlign: 'center', backgroundColor: 'rgba(0, 9, 45, 0.02)' }}>Toàn Tỉnh / Thành Phố</th>
                          <th colSpan="5" style={{ textAlign: 'center', backgroundColor: 'rgba(0, 134, 255, 0.02)' }}>Từng Xã / Phường / VPĐK</th>
                        </tr>
                        <tr>
                          <th>Số lượng</th>
                          <th>Phí / 1 Năm</th>
                          <th>Phí / 1 Tháng</th>
                          <th>Tổng Số Xã</th>
                          <th>Số lượng</th>
                          <th>Phí / 1 Xã / Năm</th>
                          <th>Phí / 1 Xã / Tháng</th>
                          <th>Phí / 1 VPĐK</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProvinces.map((p, index) => (
                          <tr key={index}>
                            <td className="cell-stt">{p.stt}</td>
                            <td className="cell-prov">{p.name}</td>
                            <td className="cell-qty">{p.prov_qty}</td>
                            <td className={`cell-price ${typeof p.prov_year === 'string' ? 'negotiable' : ''}`}>
                              {formatRawValue(p.prov_year)}
                            </td>
                            <td className={`cell-price ${typeof p.prov_month === 'string' ? 'negotiable' : ''}`}>
                              {formatRawValue(p.prov_month)}
                            </td>
                            <td className="cell-commune" style={{ fontWeight: '600' }}>{p.total_communes || '—'}</td>
                            <td className="cell-qty">{p.commune_qty || '—'}</td>
                            <td className={`cell-price ${typeof p.commune_year === 'string' ? 'negotiable' : ''}`}>
                              {formatRawValue(p.commune_year)}
                            </td>
                            <td className={`cell-price ${typeof p.commune_month === 'string' ? 'negotiable' : ''}`}>
                              {formatRawValue(p.commune_month)}
                            </td>
                            <td className="cell-price">
                              {p.vpdk_price ? `${p.vpdk_price} Trđ` : '—'}
                            </td>
                          </tr>
                        ))}
                        {filteredProvinces.length === 0 && (
                          <tr>
                            <td colSpan="10" style={{ textAlign: 'center', padding: '30px', color: '#6c757d' }}>
                              Không tìm thấy tỉnh thành phù hợp với từ khóa tìm kiếm.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="prov-info-footer">
                    {operationalPricingData.notes.map((note, idx) => (
                      <p key={idx}>{note}</p>
                    ))}
                  </div>
                </div>

              </div>

            </div>
          )}

          {/* TAB 3: BÁO GIÁ PHÍ THI CÔNG */}
          {activeTab === 'construction' && (
            <div className="construction-tab animated fadeIn">
              <div className="construction-intro">
                <h2>{constructionPricingData.title}</h2>
                <p>Mức giá thuê phân hệ thi công ưu đãi dành cho các nhà thầu thi công & chủ đầu tư</p>
              </div>

              <div className="matrix-table-card">
                <div className="table-responsive">
                  <table className="matrix-table">
                    <thead>
                      <tr>
                        <th style={{ width: '70px', textAlign: 'center' }}>STT</th>
                        <th style={{ minWidth: '320px' }}>Nội dung</th>
                        <th style={{ minWidth: '240px' }}>Ký hiệu/ghi chú</th>
                        <th style={{ minWidth: '240px' }}>Chính sách ưu đãi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {constructionPricingData.items.map((item, index) => {
                        const isSection = item.stt === '5';
                        const isSubItem = item.stt.startsWith('5.') || item.stt === '1.1' || item.stt === '1.2';

                        return (
                          <tr 
                            key={index} 
                            className={isSection ? 'section-row' : isSubItem ? 'sub-row' : ''}
                          >
                            <td className="cell-stt">{item.stt}</td>
                            <td className="cell-name" style={{ paddingLeft: isSubItem ? '32px' : '20px' }}>
                              {item.content}
                            </td>
                            <td className="cell-symbol">
                              {item.symbol || renderDash()}
                            </td>
                            <td className="cell-promo">
                              {item.promo ? (
                                <span className="promo-badge">
                                  🎁 {item.promo}
                                </span>
                              ) : renderDash()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="prov-info-footer">
                  {constructionPricingData.notes.map((note, idx) => (
                    <p key={idx}>{note}</p>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Contact Details Widget Box */}
          <div className="contact-pricing-section">
            <h3>Cần tư vấn sâu hơn về báo giá?</h3>
            <p>
              Đội ngũ chuyên gia của Vietbando luôn sẵn sàng hỗ trợ các đơn vị hành chính và nhà thầu lựa chọn 
              phương án tối ưu nhất về mặt kỹ thuật lẫn tài chính.
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                Điện thoại hỗ trợ: 036 269 7998 (Mr. Sơn - Giám đốc triển khai)
              </div>
            </div>
            <div className="btn-wrap">
              <Link to="/contactus">
                <Button buttonStyle="vbt-white-outline" buttonSize="vbt-large">
                  Gửi yêu cầu liên hệ trực tuyến
                </Button>
              </Link>
            </div>
          </div>

        </div>
      </main>

      {/* Interactive Pricing Calculator Modal Overlay */}
      {showCalculator && (
        <div className="calculator-modal-overlay" onClick={() => setShowCalculator(false)}>
          <div className="calculator-modal-content animated zoomIn" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setShowCalculator(false)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            
            <div className="calculator-card">
              <h3>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
                  <line x1="9" y1="22" x2="9" y2="16"></line>
                  <line x1="8" y1="6" x2="16" y2="6"></line>
                  <line x1="16" y1="22" x2="16" y2="16"></line>
                  <line x1="9" y1="16" x2="15" y2="16"></line>
                  <circle cx="9" cy="11" r="1"></circle>
                  <circle cx="15" cy="11" r="1"></circle>
                  <circle cx="9" cy="15" r="1"></circle>
                  <circle cx="15" cy="15" r="1"></circle>
                </svg>
                Công cụ tính phí vận hành
              </h3>
              
              {/* Select Province */}
              <div className="form-group">
                <label>1. Chọn Tỉnh / Thành Phố</label>
                <select 
                  className="select-input"
                  value={selectedProvinceName}
                  onChange={(e) => setSelectedProvinceName(e.target.value)}
                >
                  {operationalPricingData.provinces.map((p, idx) => (
                    <option key={idx} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Row of Toggles */}
              <div className="calc-row-toggles">
                {/* Toggle Pricing Method */}
                <div className="form-group">
                  <label>2. Phương Án Ký</label>
                  <div className="method-toggles">
                    <button 
                      className={`method-btn ${calcMethod === 'province' ? 'active' : ''}`}
                      onClick={() => setCalcMethod('province')}
                    >
                      Toàn tỉnh
                    </button>
                    <button 
                      className={`method-btn ${calcMethod === 'commune' ? 'active' : ''}`}
                      onClick={() => {
                        setCalcMethod('commune');
                        if (activeProvince) {
                          setCommunesCount(activeProvince.total_communes);
                        }
                      }}
                    >
                      Theo xã
                    </button>
                  </div>
                </div>

                {/* Toggle Billing Term */}
                <div className="form-group">
                  <label>3. Chu Kỳ</label>
                  <div className="method-toggles">
                    <button 
                      className={`method-btn ${calcBilling === 'yearly' ? 'active' : ''}`}
                      onClick={() => setCalcBilling('yearly')}
                    >
                      Theo năm
                    </button>
                    <button 
                      className={`method-btn ${calcBilling === 'monthly' ? 'active' : ''}`}
                      onClick={() => setCalcBilling('monthly')}
                    >
                      Theo tháng
                    </button>
                  </div>
                </div>
              </div>

              {/* Inputs Row - only show if commune method */}
              {calcMethod === 'commune' && activeProvince && (
                <div className="calc-inputs-row animated fadeIn">
                  {/* Commune input */}
                  <div className="form-group">
                    <label>4. Số Xã / Phường (Max: {activeProvince.total_communes})</label>
                    <div className="commune-slider-wrap">
                      <input 
                        type="range" 
                        min="1" 
                        max={activeProvince.total_communes || 100} 
                        value={communesCount}
                        onChange={(e) => setCommunesCount(parseInt(e.target.value))}
                      />
                      <input 
                        type="number" 
                        className="number-input"
                        min="1" 
                        max={activeProvince.total_communes || 100}
                        value={communesCount}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (val > 0) {
                            setCommunesCount(Math.min(val, activeProvince.total_communes));
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* VPĐK input */}
                  <div className="form-group">
                    <label>5. Số VPĐK (Chi nhánh)</label>
                    <input 
                      type="number" 
                      className="number-input"
                      min="0"
                      value={vpdkCount}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (val >= 0) setVpdkCount(val);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Result Box */}
              <div className="calc-result-box">
                {calcMethod === 'commune' && (
                  <>
                    <div className="calc-row">
                      <span>Chi phí xã ({communesCount} xã):</span>
                      <span>{formatCurrency(calcResult.subtotalCommunes)}</span>
                    </div>
                    <div className="calc-row">
                      <span>Chi phí VPĐK ({vpdkCount} đơn vị):</span>
                      <span>{formatCurrency(calcResult.subtotalVpdk)}</span>
                    </div>
                  </>
                )}
                
                <div className="calc-row calc-total">
                  <span>Tổng chi phí ước tính:</span>
                  <span className="total-val">
                    {calcMethod === 'province' && typeof (calcBilling === 'yearly' ? activeProvince.prov_year : activeProvince.prov_month) === 'string'
                      ? 'Thỏa thuận'
                      : formatCurrency(calcResult.total)
                    }
                  </span>
                </div>
              </div>

              <div className="btn-contact">
                <Link to="/contactus">
                  <Button buttonStyle="vbt-blue-solid" buttonSize="vbt-large">
                    Liên hệ nhận báo giá chi tiết
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
